import classNames from 'classnames'
import styles from './IconButton.module.css'
import { type AllowedButtonTags, Button, headlessButtonProps } from '../Button/server'
import { type CSSProperties, forwardRef } from 'react'
import { Icon, type IconProps } from '../Icon'
import { type IconButtonProps, type IconButtonType } from './types'
import type { Sizing } from '../types'

const iconSizes = {
  small: '200',
  default: '300',
  input: '300',
  large: '400',
} as const

function IconSlot({ enabled, ...props }: Omit<IconProps, 'size'> & { enabled?: boolean; size: Sizing }) {
  const cssVar = `var(--betterplace-sizing-${props.size})`
  return (
    <span
      className={classNames(styles.iconSlot, { [styles.hasIcon!]: enabled })}
      style={{ '--icon-size': cssVar, 'width': cssVar } as CSSProperties}
    >
      {enabled && <Icon {...props} />}
    </span>
  )
}

// eslint-disable-next-line import/exports-last
function headlessIconButtonPropsInternal<T extends AllowedButtonTags>({
  children: children_,
  size = 'default',
  className: userClassName,
  iconName,
  iconPosition = 'left',
  iconProps = {},
  ...props
}: IconButtonProps<T>) {
  const className = classNames(styles.iconButton, {
    [userClassName as string]: userClassName,
    [styles.iconOnly as string]: !children_ && !!iconName,
  })

  const hasIcon = !!iconName
  const hasIconRight = hasIcon && iconPosition === 'right'
  const hasIconLeft = hasIcon && iconPosition === 'left'
  const iconSize = iconProps.size ?? iconSizes[size]
  const children = (
    <>
      <IconSlot {...iconProps} name={iconName!} size={iconSize} enabled={hasIconLeft} />
      {children_}
      <IconSlot {...iconProps} name={iconName!} size={iconSize} enabled={hasIconRight} />
    </>
  )

  return { ...props, children, className, size }
}

// eslint-disable-next-line import/exports-last
export function headlessIconButtonProps<T extends AllowedButtonTags>(props: IconButtonProps<T>) {
  const buttonProps = headlessIconButtonPropsInternal<T>(props)
  const props_ = headlessButtonProps<T>(buttonProps)
  return props_
}

const IconButton = forwardRef<HTMLButtonElement, IconButtonProps<'button'>>(function IconButton<
  T extends AllowedButtonTags = 'button',
>(props: IconButtonProps<T>, ref: React.Ref<HTMLButtonElement>) {
  const { children, ...props_ } = headlessIconButtonPropsInternal(props)

  return (
    <Button<T> ref={ref} {...props_}>
      {children}
    </Button>
  )
}) as IconButtonType

export default IconButton
